import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Observable, tap} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ConfigService} from '../services/config.service';
import {EventsEmitterService} from '../services/emitter.service';
import {LogService} from '../services/log.service';
import {SecurityService} from '../services/security.service';

@Injectable({providedIn: 'root'})
export class GridService extends SecurityService {
  protected serviceName = '';

  constructor(
    http: HttpClient,
    configService: ConfigService,
    eventsEmitterService: EventsEmitterService,
    router: Router,
    logger: LogService,
    @Inject(String) serviceName: string,
  ) {
    super(http, configService, eventsEmitterService, router, logger);

    this.serviceName = serviceName;
  }

  setServiceName(serviceName: string) {
    this.serviceName = serviceName;
  }

  /**
   * Filter params
   *
   * @param params { 0-n?:any }
   */
  initData(params: any): Observable<any> {
    let serviceUrl = this.baseUrl + this.serviceName + '/init?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

  /**
   * @param params { group:string, ...0-n?:any }
   */
  getGridActions(params: any): Observable<any> {
    let serviceUrl =
      this.baseUrl + this.serviceName + '/gridActions?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

  /**
   * @param params { group:string, ...0-n?:any }
   */
  getColumnDefinition(params: any): Observable<any> {
    let serviceUrl =
      this.baseUrl + this.serviceName + '/columnDefinitions?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

  /**
   * @param params { cookieId:Number, ...0-n?:any }
   */
  getDataCount(params: any): Observable<any> {
    let serviceUrl = this.baseUrl + this.serviceName + '/count?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

  /**
   * @param params { start:Number, end:number, order:string, cookieId:Number, ...0-n?:any }
   */
  getData(params: any): Observable<any> {
    let serviceUrl = this.baseUrl + this.serviceName + '/data?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

    /**
   * @param params { group:string, ...0-n?:any }
   */
    get4eyesRequest(params: any): Observable<any> {
      let serviceUrl = this.baseUrl + 'FourEyesService/getRequest?requestId=' + params;
  
      return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
    }

  /**
   * @param params { cookieId:Number, ...0-n?:any }
   */
  getFooter(params: any): Observable<any> {
    let serviceUrl = this.baseUrl + this.serviceName + '/footer?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

  /**
   * @param params { cookieId:Number, ...0-n?:any }
   */
  getHeader(params: any): Observable<any> {
    let serviceUrl = this.baseUrl + this.serviceName + '/header?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(this.extractData), catchError(this.handleError));
  }

  /**
   * @param params { cookieId:Number, ...0-n?:any }
   */
  getTxt(params: any): Observable<any> {
    let serviceUrl =
      this.baseUrl + this.serviceName + '/downloadTxt?' + this.urlParamsToString(params);
    return this.http
      .get(serviceUrl, {
        observe: 'response',
        responseType: 'text',
        headers: this.setAuthorizationHeader(null),
      })
      .pipe(
        tap((res: HttpResponse<string>) => this.extractTxt(res)),
        catchError(super.handleError),
      );
  }

  /**
   * @param params { cookieId:Number, ...0-n?:any }
   */
  getCsv(params: any): Observable<any> {
    let serviceUrl =
      this.baseUrl + this.serviceName + '/downloadCsv?' + this.urlParamsToString(params);

    return this.http
      .get(serviceUrl, {
        responseType: 'text',
        observe: 'response',
        headers: this.setAuthorizationHeader(null),
      })
      .pipe(
        tap((res: HttpResponse<any>) => this.extractCsv(res)),
        catchError(this.handleError),
      );
  }

  /**
   * @param params { cookieId:Number, ...0-n?:any }
   */
  getXls(params: any): Observable<any> {
    let serviceUrl =
      this.baseUrl + this.serviceName + '/downloadXls?' + this.urlParamsToString(params);

    return this.http
      .get(serviceUrl, {
        responseType: 'blob',
        observe: 'response',
        headers: this.setAuthorizationHeader(null),
      })
      .pipe(
        tap((res: HttpResponse<Blob>) => super.extractXls(res)),
        catchError(super.handleError),
      );
  }

  /**
   * @param params { cookieId:Number, ...0-n?:any }
   */
  getPdf(params: any): Observable<any> {
    let serviceUrl =
      this.baseUrl + this.serviceName + '/downloadPdf?' + this.urlParamsToString(params);

    return this.http
      .get(serviceUrl, {
        responseType: 'blob',
        observe: 'response',
        headers: this.setAuthorizationHeader(null),
      })
      .pipe(
        tap((res: HttpResponse<Blob>) => this.extractPdf(res)),
        catchError(this.handleError),
      );
  }

  //Grid checkbox toggling
  /**
   * @param params { cookieId:Number }
   */
  selectAll(params: any): Observable<any> {
    let serviceUrl =
      this.baseUrl + this.serviceName + '/selectAll?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(this.extractData), catchError(this.handleError));
  }

  /**
   * @param params { cookieId:Number }
   */
  deselectAll(params: any): Observable<any> {
    let serviceUrl =
      this.baseUrl + this.serviceName + '/deselectAll?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

  /**
   * @param params { cookieId:Number, 0-n?:(Object's PK) }
   */
  select(params: any): Observable<any> {
    let serviceUrl = this.baseUrl + this.serviceName + '/select?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

  /**
   * @param params { cookieId:Number, 0-n?:(Object's PK) }
   */
  deselect(params: any): Observable<any> {
    let serviceUrl =
      this.baseUrl + this.serviceName + '/deselect?' + this.urlParamsToString(params);

    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

  protected urlParamsToString(params: any) {
    let urlParams = '';
    Object.keys(params).forEach((param, index) => {
      urlParams += param + '=' + this.sanitize(params[param]) + '&';
    });

    return urlParams + 'langId=' + localStorage.getItem('langId');
  }

  //URLparams sanitization
  protected sanitize(data: any) {
    return data != null ? data : '';
  }
}
